import { isClassicRelativeDateTime } from 'cadenza/relative-date-time/is-classic-relative-date-time';
import type { RelativeDateTime } from 'cadenza/relative-date-time/relative-date-time';
import type { RelativeDateTimeInterval } from 'cadenza/relative-date-time/relative-date-time-interval';

export function calcRelativeStartAndEndDate (
  relativeDateTimeOrInterval: RelativeDateTime | RelativeDateTimeInterval,
  now = new Date()): [ Date, Date ] {
  if (isClassicRelativeDateTime(relativeDateTimeOrInterval)) {
    return calcRelativeStartEndDateClassic(relativeDateTimeOrInterval as RelativeDateTime, now);
  }

  const { from, to, unit } = relativeDateTimeOrInterval as RelativeDateTimeInterval;
  const fromOffset = parseInt(from.toString());
  const toOffset = parseInt(to.toString());

  const newDate = () => new Date(now); // To always use the same time, no matter how long execution takes

  switch (unit) {
    case 'SECOND': {
      const startDate = newDate();
      startDate.setTime(startDate.getTime() + (fromOffset * 1000));
      startDate.setHours(startDate.getHours(), startDate.getMinutes(), startDate.getSeconds(), 0);
      const endDate = newDate();
      endDate.setTime(endDate.getTime() + (toOffset * 1000));
      endDate.setHours(endDate.getHours(), endDate.getMinutes(), endDate.getSeconds(), 999);
      return [ startDate, endDate ];
    }
    case 'MINUTE': {
      const startDate = newDate();
      startDate.setTime(startDate.getTime() + (fromOffset * 1000 * 60));
      startDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
      const endDate = newDate();
      endDate.setTime(endDate.getTime() + (toOffset * 1000 * 60));
      endDate.setHours(endDate.getHours(), endDate.getMinutes(), 59, 999);
      return [ startDate, endDate ];
    }
    case 'HOUR': {
      const startDate = newDate();
      startDate.setTime(startDate.getTime() + (fromOffset * 1000 * 60 * 60));
      startDate.setHours(startDate.getHours(), 0, 0, 0);
      const endDate = newDate();
      endDate.setTime(endDate.getTime() + (toOffset * 1000 * 60 * 60));
      endDate.setHours(endDate.getHours(), 59, 59, 999);
      return [ startDate, endDate ];
    }
    case 'DAY': {
      const startDate = newDate();
      startDate.setDate(startDate.getDate() + fromOffset);
      const endDate = newDate();
      endDate.setDate(endDate.getDate() + toOffset);
      return getStartEndTime(startDate, endDate);
    }
    case 'WEEK': {
      const fromDate = new Date(newDate().setDate(newDate().getDate() + 7 * fromOffset));
      const toDate = new Date(newDate().setDate(newDate().getDate() + 7 * toOffset));
      return getStartAndEndDayOfTheWeek(fromDate, toDate);
    }
    case 'MONTH': {
      const startDayOfTheMonth = getStartDayOfTheMonth(newDate());
      const fromDate = new Date(startDayOfTheMonth);
      fromDate.setMonth(fromDate.getMonth() + fromOffset);
      const toDate = startDayOfTheMonth;
      toDate.setMonth(toDate.getMonth() + toOffset);
      return getStartAndEndDayOfTheMonth(fromDate, toDate);
    }
    case 'QUARTER': {
      const startDayOfTheQuarter = getStartDayOfTheQuarter(newDate());
      const fromDate = new Date(startDayOfTheQuarter);
      fromDate.setMonth(fromDate.getMonth() + 3 * fromOffset);
      const toDate = startDayOfTheQuarter;
      toDate.setMonth(toDate.getMonth() + 3 * toOffset);
      return getStartAndEndDayOfTheQuarter(fromDate, toDate);
    }
    case 'YEAR': {
      const startDayOfTheYear = getStartDayOfTheYear(newDate());
      const fromDate = new Date(startDayOfTheYear);
      fromDate.setFullYear(fromDate.getFullYear() + fromOffset);
      const toDate = startDayOfTheYear;
      toDate.setFullYear(toDate.getFullYear() + toOffset);
      return getStartAndEndDayOfTheYear(fromDate, toDate);
    }
    default:
      throw new Error(`Unsupported unit: '${unit}'`);
  }
}

function calcRelativeStartEndDateClassic (
  { amount: relativeAmount, unit }: RelativeDateTime,
  now: Date): [ Date, Date ] {
  const newDate = () => new Date(now);

  switch (unit) {
    case 'DAY': {
      const startDate = newDate();
      startDate.setDate(startDate.getDate() + relativeAmount);
      const endDate = newDate();
      endDate.setDate(endDate.getDate() + relativeAmount);
      return getStartEndTime(startDate, endDate);
    }
    case 'WEEK': {
      const date = new Date(newDate().setDate(newDate().getDate() + 7 * relativeAmount));
      return getStartAndEndDayOfTheWeek(date, date);
    }
    case 'MONTH': {
      const date = getStartDayOfTheMonth(newDate());
      date.setMonth(date.getMonth() + relativeAmount);
      return getStartAndEndDayOfTheMonth(date, date);
    }
    case 'QUARTER': {
      const date = getStartDayOfTheQuarter(newDate());
      date.setMonth(date.getMonth() + 3 * relativeAmount);
      return getStartAndEndDayOfTheQuarter(date, date);
    }
    case 'YEAR': {
      const date = getStartDayOfTheYear(newDate());
      date.setFullYear(date.getFullYear() + relativeAmount);
      return getStartAndEndDayOfTheYear(date, date);
    }
    default:
      throw new Error(`Unsupported unit: '${unit}'`);
  }
}

function getStartAndEndDayOfTheWeek (from: Date, to: Date) {
  const start = from.getDate() - from.getDay() + 1;
  const startDate = new Date(new Date(from).setDate(start));
  const end = to.getDate() - to.getDay() + 7;
  const endDate = new Date(new Date(to).setDate(end));
  return getStartEndTime(startDate, endDate);
}

function getStartAndEndDayOfTheMonth (from: Date, to: Date) {
  return getStartEndTime(
    getStartDayOfTheMonth(from),
    getEndDayOfTheMonth(to));
}

function getStartDayOfTheMonth (date: Date) {
  const startDate = new Date(date);
  startDate.setDate(1);
  return startDate;
}

function getEndDayOfTheMonth (date: Date) {
  const startDate = getStartDayOfTheMonth(date);
  const endDate = new Date(startDate);
  endDate.setDate(getDaysInMonth(startDate));
  return endDate;
}

function getDaysInMonth (date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function getStartAndEndDayOfTheQuarter (from: Date, to: Date) {
  return getStartEndTime(
    getStartDayOfTheQuarter(from),
    getEndDayOfTheQuarter(to));
}

function getStartDayOfTheQuarter (date: Date) {
  const startDate = getStartDayOfTheMonth(date);
  const quarter = Math.floor(date.getMonth() / 3);
  startDate.setMonth(3 * (quarter));
  startDate.setDate(1);
  return startDate;
}

function getEndDayOfTheQuarter (date: Date) {
  const startDate = getStartDayOfTheQuarter(date);
  const endDate = new Date(startDate);
  endDate.setMonth(startDate.getMonth() + 2);
  endDate.setDate(getDaysInMonth(endDate));
  return endDate;
}

function getStartAndEndDayOfTheYear (from: Date, to: Date) {
  return getStartEndTime(
    getStartDayOfTheYear(from),
    getEndDayOfTheYear(to));
}

function getStartDayOfTheYear (date: Date) {
  const startDate = new Date(date);
  startDate.setDate(1);
  startDate.setMonth(0);
  return startDate;
}

function getEndDayOfTheYear (date: Date) {
  const startDate = getStartDayOfTheYear(date);
  const endDate = new Date(startDate);
  endDate.setDate(31);
  endDate.setMonth(11);
  return endDate;
}

function getStartEndTime (startDate: Date, endDate: Date): [ Date, Date ] {
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  return [ startDate, endDate ];
}

